import { Record as record, Set, Map, List } from 'immutable';
import { sum } from '../../helpers/Immutable';

import { construct, resolve } from '../../helpers/LinkMapper';

import { PlusBusDirections } from '../../constants/AppConstants';
import Order from '../Order';
import OrderTrip from '../order/Trip';
import OrderFare from '../order/Fare';

const matchesPlusbusTicket = (ticket) => (plusBusTicket) => {
    const sameAdults = plusBusTicket.adults === ticket.adults;
    const sameChildren = plusBusTicket.children === ticket.children;
    const passengersMatch = sameAdults || sameChildren;
    const ticketHasRailcard = !!ticket.railcard;
    const plusBusHasRailcard = !!plusBusTicket.railcard;

    if (ticketHasRailcard && !plusBusHasRailcard) {
        return false;
    } else if (!ticketHasRailcard && plusBusHasRailcard) {
        return false;
    } else if (ticketHasRailcard && plusBusHasRailcard) {
        return passengersMatch &&
            ticket.railcard.code === plusBusTicket.railcard.code
            && !plusBusTicket.isRefunded;
    }

    return passengersMatch && !plusBusTicket.isRefunded;
};

const matchesPlusbusFare = (fare, trip) => (plusBus) => {
    const outwardMatches = trip.getPlusBus().matchesOriginOrDestination(plusBus, PlusBusDirections.OUTWARD);
    const inwardMatches = trip.getPlusBus().matchesOriginOrDestination(plusBus, PlusBusDirections.RETURN);
    const sameDay = fare.getValidFrom().diff(plusBus.getValidFrom(), 'days') === 0;

    return (outwardMatches || inwardMatches)
        && sameDay && plusBus.hasAmendableTickets();
};

function fareSetter (fares, fare, ticket) {
    if (!ticket) {
        return fares;
    }

    if (fares.has(fare)) {
        return fares.set(fare, fares.get(fare).add(ticket));
    }

    return fares.set(fare, Set.of(ticket));
}

function fareRemover (fares, fare, ticket) {
    fares = fares.set(fare, fares.get(fare).remove(ticket));

    if (fares.get(fare).size === 0) {
        return fares.remove(fare);
    }

    return fares;
}

const defaults = {
    order: null,
    trip: new OrderTrip(),
    fares: new Map(),
    selectedRailcards: {},
};

class AmendRequest extends record(defaults) {
    static fromJSON (json) {
        return construct(AmendRequest, json, {
            order: resolve.as(Order),
            trip: resolve.as(OrderTrip),
            fare: resolve.as(OrderFare),
            selectedRailcards: resolve.as(Object),
        });
    }

    isValid () {
        const fares = this.fares.keySeq().toSet();
        return !!fares.size && fares.every((fare) => !this.getFareWarnings(fare));

    }

    hasOutwardFare() {
        const {trip, fares} = this;
        return fares && trip && fares.has(trip.fares.outwardSingle);
    }

    hasReturnFare() {
        const {trip, fares} = this;
        return fares && trip && fares.has(trip.fares.returnSingle);
    }

    getAllValidRailcards ({ passengers }) {
        const selectedRailcardsArray = Object.values(this.selectedRailcards)
            .map(railcard => ({
                code: railcard.code,
                quantity: Math.min(passengers, railcard.quantity),
            }));
        return new List(selectedRailcardsArray);
    }

    areRailcardsValidWithFare (fare) {
        const selectedTickets = this.fares.get(fare);
        const railcards = fare.getRailcards(selectedTickets);
        return railcards.every((railcard) => {
            const railcardTickets = fare.getTicketsWithRailCard(railcard, selectedTickets);
            return railcardTickets.isValid();
        });
    }

    getFareWarnings (fare) {
        const selectedTickets = this.fares.get(fare);
        const remainingTickets = fare.tickets.toSet().subtract(selectedTickets);
        const railcards = fare.getRailcards(remainingTickets);
        let warnings = railcards.reduce((result, railcard) => {
            const railcardTickets = fare.getTicketsWithRailCard(railcard, remainingTickets);
            return railcardTickets.isValid() ? result : result.add(`account.orders.refund.warning.${ railcard.code }`);
        }, new Set());

        return warnings.size ? warnings : null;
    }

    setOrder (order) {
        return this.set('order', order);
    }

    transformFares () {
        return this.fares.keySeq().toSet()
            .map(
                (item) =>
                    item.set('tickets', this.fares.get(item).toList)
            ).toList();
    }

    setPlusBusTickets (plusBusFares, ticket, fares) {
        // Automaticaly select the equivalent plusbus fare for a given
        // ticket. When amending, it's like for like, so if a fare has
        // an equivalent plusbus fare that must be selected too. We
        // filter out so that only adults or children are selected.
        let next = fares;
        plusBusFares.map((plusBusFare) => {
            const selectedTickets = fares.get(plusBusFare);
            const plusBusTicket = plusBusFare.tickets
                .toSet().subtract(selectedTickets)
                .filter(matchesPlusbusTicket(ticket)).first();
            next = fareSetter(next, plusBusFare, plusBusTicket);
        });

        return next;
    }

    removePlusBusTicket (plusBusFares, ticket, fares) {
        let next = fares;
        plusBusFares
            .map((plusBusFare) => {
                const selectedTickets = fares.get(plusBusFare);
                const plusBusTicket = selectedTickets
                      .filter(matchesPlusbusTicket(ticket)).first();
                next = fareRemover(next, plusBusFare, plusBusTicket);
              });
        return next;
    }

    resetSelectedRailcards () {
        Object.keys(this.selectedRailcards).forEach(key => delete this.selectedRailcards[key]);
    }

    addSelectedRailcards (fare, ticket) {
        if (!this.areRailcardsValidWithFare(fare) || fare.isPlusBus()) {
            return;
        }
        const cards = fare.getRailcards();
        if (!(cards && cards.size)) {
            return;
        }
        cards.forEach(card => {
            if (this.selectedRailcards[card.code]) {
                this.selectedRailcards[card.code] = {
                    code: card.code,
                    quantity: this.selectedRailcards[card.code].quantity + ticket.adults + ticket.children,
                };
            } else {
                this.selectedRailcards[card.code] = {
                    code: card.code,
                    quantity: 1
                };
            }
        });
    }

    addTicket (fare, ticket) {
        this.addSelectedRailcards(fare, ticket);

        let fares = fareSetter(this.fares, fare, ticket);

        if (this.trip.hasPlusBus()) {
            const plusBusFares = this.trip.getPlusBusFares()
                .filter(matchesPlusbusFare(fare, this.trip));
            fares = this.setPlusBusTickets(plusBusFares, ticket, fares);
        }

        return this.set('fares', fares);
    }

    removeSelectedRailcards (fare, ticket) {
        if (!this.areRailcardsValidWithFare(fare) || fare.isPlusBus()) {
            return;
        }
        const cards = fare.getRailcards();
        if (!(cards && cards.size)) {
            return;
        }
        cards.forEach(card => {
            if (!this.selectedRailcards[card.code]) {
                return;
            }
            const newQuantity = this.selectedRailcards[card.code].quantity - ticket.adults - ticket.children;
            if (newQuantity > 0) {
                this.selectedRailcards[card.code] = {
                    code: card.code,
                    quantity: newQuantity
                };
            } else {
                delete this.selectedRailcards[card.code];
            }
        });
    }

    removeTicket (fare, ticket) {
        this.removeSelectedRailcards(fare, ticket);

        let fares = fareRemover(this.fares, fare, ticket);

        if (this.trip.hasPlusBus() ) {
            const plusBusFares = this.trip.getPlusBusFares()
                  .filter(matchesPlusbusFare(fare, this.trip));
            fares = this.removePlusBusTicket(plusBusFares, ticket, fares);
        }

        return this.set('fares', fares);
    }

    getAdults(fare) {
        return sum(this.fares.get(fare), 'adults');
    }

    getChildren(fare) {
        return sum(this.fares.get(fare), 'children');
    }

    totalAdminFee (config) {
        const ticketCount = this.getTicketCount();
        return ticketCount * config.adminFeePerTicket + config.adminFeePerOrder;
    }

    getAmendedTickets () {
        return this.fares.toList().flatten(1);
    }

    getAmendedTicketIds () {
        return this.getAmendedTickets().map(ticket => ticket.id);
    }

    getTicketCount () {
        return this.fares.keySeq()
            .filter(x => !x.isPlusBus())
            .map(x => this.fares.get(x))
            .toList().flatten(1).size;
    }

    isSelected(fare) {
        return this.fares.has(fare);
    }

    isAmendingInwardReturnOnly () {
        return this.hasReturnFare() && !this.hasOutwardFare();
    }

    // this method only returns an accurate description once the request's order has been updated with new trip
    getAmendmentDescription () {
        if (!this.trip.isReturn()) {
            return 'single-outward';
        }

        const amendedPortion = this.order.trips.last().get('fares');

        if (amendedPortion.get('outwardSingle') && amendedPortion.get('returnSingle')) {
            return 'return-both';
        }

        const amendedOrigin = amendedPortion.get('outwardSingle').origin.nlc;
        const amendedDestination = amendedPortion.get('outwardSingle').destination.nlc;
        const originalOrigin = this.trip.fares.outwardSingle.origin.nlc;
        const originalDestination = this.trip.fares.outwardSingle.destination.nlc;

        if (amendedOrigin === originalOrigin
                && amendedDestination === originalDestination) {
           return 'return-outward';
        }
        if (amendedOrigin === originalDestination
                && amendedDestination === originalOrigin) {
            return 'return-inward';
        }
        console.error('Amend bookings description not found');
        return '';
    }

    getTotalPrice (direction) {
        const fares = this.fares.keySeq().toSet();
        const fare = direction === 'inward' ? fares.last() : fares.first();
        const tickets = this.fares.get(fare);
        return sum(tickets, 'price');
    }

    getHighestTicketPrice (direction) {
        const fares = this.fares.keySeq().toSet();
        const fare = direction === 'inward' ? fares.last() : fares.first();
        const tickets = this.fares.get(fare).sortBy(ticket => ticket.price);
        return tickets.last().price;
    }

    getTicketTypeCode (direction) {
        let list;
        if (direction === 'inward') {
            list = this.trip.fares.returnSingle;
        } else {
            list = this.trip.fares.outwardSingle;
        }
        return list.ticketType.code;
    }
}

export default AmendRequest;



// WEBPACK FOOTER //
// ./src/records/amend/Request.js